import React, { useMemo, useState, useEffect } from "react";
import "./AtlasPromptLibrary.css";
import Layout from "../../Components/Layout/Layout";

export const promptSections = [
  {
    id: "lead-gen",
    title: "Lead Gen",
    description:
      "Find companies not yet in Ohio that fit sector criteria",
    items: [
      {
        sector: "Semiconductors & Microelectronics",
        prompt:
          "List 5 semiconductor fabrication companies with revenue over $100M that are expanding U.S. operations and not currently located in Ohio.",
      },
      {
        sector: "Logistics & Distribution",
        prompt:
          "List third-party logistics companies with large Midwest distribution operations that have not announced a presence in Ohio. For each, include estimated employee count, states where they operate major facilities, and any recent expansion announcements. Focus on companies with 500 or more employees.",
      },
      {
        sector: "Financial Services & Fintech",
        prompt:
          "Find 2 fintech companies headquartered outside Ohio with revenue between $25M–$200M that are in growth stages and have a Midwest customer base.",
      },
      {
        sector: "Life Sciences & Biohealth",
        prompt:
          "Identify 3 clinical-stage biotech companies with recent Series B or C funding that do not have Ohio operations but are expanding East of the Mississippi.",
      },
      {
        sector: "Food & Agribusiness",
        prompt:
          "Find 5 specialty food manufacturers with $50M+ revenue located in the Southeast or Mid-Atlantic that lack Midwest production capacity.",
      },
      {
        sector: "Aerospace & Defense",
        prompt:
          "List defense contractors with active DoD contracts over $10M that have no Ohio presence but supply to Wright-Patterson Air Force Base vendors.",
      },
    ],
  },
  {
    id: "decision-makers",
    title: "Decision Makers",
    description:
      "Identify key contacts at target companies by JOI Score or sector fit",
    items: [
      {
        sector: "Advanced Manufacturing",
        prompt:
          "Identify the VP of Operations and Chief Supply Chain Officer at the top 15 advanced manufacturing firms ranked by JOI Score considering Ohio expansion.",
      },
      {
        sector: "Semiconductors & Microelectronics",
        prompt:
          "Find C-suite and site selection decision makers at the 5 highest-scored semiconductor companies currently evaluating U.S. fab expansion sites.",
      },
      {
        sector: "Cybersecurity & IT",
        prompt:
          "What job titles and roles typically lead data center siting and infrastructure expansion decisions at mid-to-large cybersecurity and IT firms? List the top 5–7 titles to target, and suggest which types of cybersecurity companies (by segment or revenue range) are most likely evaluating new Midwest locations.",
      },
      {
        sector: "Life Sciences & Biohealth",
        prompt:
          "List 5 Chief Scientific Officers and VP of Research roles at Ohio-eligible biohealth companies with active clinical trials in the Midwest.",
      },
      {
        sector: "Logistics & Distribution",
        prompt:
          "Who are the key executives at major e-commerce fulfillment companies responsible for network planning and distribution hub decisions? Include their titles and companies.",
      },
      {
        sector: "Financial Services & Fintech",
        prompt:
          "Identify 5 Chief Technology Officers and expansion leads at fintech firms with JOI Scores above threshold that are building out operations teams.",
      },
    ],
  },
  {
    id: "legislation",
    title: "Legislation",
    description:
      "Track regulatory and policy changes affecting Ohio-relevant sectors",
    items: [
      {
        sector: "Semiconductors & Microelectronics",
        prompt:
          "Give me a report summarizing the current status of CHIPS Act implementation funding, eligibility rules, and any proposed amendments impacting domestic semiconductor fab siting.",
      },
      {
        sector: "Life Sciences & Biohealth",
        prompt:
          "Give me a report outlining recent FDA regulatory changes and federal R&D tax credit updates that affect biohealth manufacturing and clinical trial operations in Midwest states.",
      },
      {
        sector: "Food & Agribusiness",
        prompt:
          "Search for recent USDA and EPA regulatory updates affecting large-scale food and agribusiness operations, with a focus on water use permitting, PFAS standards, and processing facility compliance. Write a report summarizing key rule changes and flag any that have specific implications for Ohio-based operations or that differ from neighboring Midwest states.",
      },
      {
        sector: "Logistics & Distribution",
        prompt:
          "Identify recent federal infrastructure bill provisions and DOT regulations affecting last-mile delivery infrastructure investment and intermodal logistics hubs in Ohio.",
      },
      {
        sector: "Cybersecurity & IT",
        prompt:
          "Summarize new federal cybersecurity mandates (CISA, FISMA updates) and state-level data privacy laws that would affect IT companies considering Ohio data center operations.",
      },
      {
        sector: "Advanced Manufacturing",
        prompt:
          "Outline recent changes to Inflation Reduction Act advanced manufacturing tax credits, including Section 45X provisions, and their implications for Ohio-based production.",
      },
    ],
  },
  {
    id: "competitive-intel",
    title: "Competitive Intel",
    description:
      "Benchmark Ohio's position against competing states by sector",
    items: [
      {
        sector: "Semiconductors & Microelectronics",
        prompt:
          "Create a competitive landscape report comparing Ohio vs. Arizona and Texas for semiconductor investment attraction, including incentive packages, workforce pipelines, and recent wins.",
      },
      {
        sector: "Life Sciences & Biohealth",
        prompt:
          "Compare Ohio's biohealth ecosystem (talent, research institutions, incentives) against North Carolina's Research Triangle and Pennsylvania's Philadelphia corridor.",
      },
      {
        sector: "Financial Services & Fintech",
        prompt:
          "Write me a report on how Ohio's fintech environment compares to Georgia (Atlanta) and Illinois (Chicago) in terms of regulatory climate, talent availability, and startup ecosystem.",
      },
      {
        sector: "Food & Agribusiness",
        prompt:
          "Build a competitive comparison of Ohio vs. Indiana and Illinois for agribusiness and food processing attraction, covering tax incentives, infrastructure, and recent facility announcements.",
      },
      {
        sector: "Logistics & Distribution",
        prompt:
          "Give me a report on Ohio's competitive position for distribution hub siting vs. Indiana and Kentucky, including highway/rail access, labor costs, and recent Amazon/3PL facility decisions.",
      },
      {
        sector: "Aerospace & Defense",
        prompt:
          "Compare Ohio's aerospace and defense industry footprint vs. Virginia and Alabama, covering DoD spending, contractor presence, and proximity to military installations.",
      },
    ],
  },
  {
    id: "industry-news",
    title: "Industry News",
    description:
      "Monitor sector developments, partnerships, and workforce trends",
    items: [
      {
        sector: "Semiconductors & Microelectronics",
        prompt:
          "Analyze recent news on Intel's Ohio fab progress and its downstream impact on supplier attraction, workforce development, and related semiconductor ecosystem growth in Central Ohio.",
      },
      {
        sector: "Life Sciences & Biohealth",
        prompt:
          "Summarize recent M&A activity, clinical trial announcements, and major hiring news among biohealth companies with Ohio ties or Midwest expansion plans.",
      },
      {
        sector: "Advanced Manufacturing",
        prompt:
          "Identify the top 10 advanced manufacturing facility announcements in the U.S. in the past 6 months and assess which Ohio regions could have competed for those projects.",
      },
      {
        sector: "Logistics & Distribution",
        prompt:
          "Analyze recent news on supply chain reshoring trends and new distribution center openings in the Midwest, highlighting job creation impacts and site selection factors.",
      },
      {
        sector: "Food & Agribusiness",
        prompt:
          "Give me a summarized report on recent partnerships, plant expansion news, and investment announcements among major food and agribusiness companies with Ohio operations or supply chain ties.",
      },
      {
        sector: "Cybersecurity & IT",
        prompt:
          "Review recent data center and tech campus announcements in Ohio and neighboring states, identifying trends in power requirements, workforce sourcing, and state incentive structures.",
      },
    ],
  },
  {
    id: "other",
    title: "Other Prompts",
    description:
      "Additional prompts to consider for targeted research and outreach",
    items: [
      {
        prompt:
          "I have a meeting with AlphaSense (https://www.alpha-sense.com/). Create a lead intelligence report. Include decision maker contact details and connection paths to those listed decision makers to JobsOhio associates.",
      },
      {
        prompt:
          "Provide a list of AI robotic manufacturing companies outside of Ohio who have received funding within 2026",
      },
      {
        prompt:
          "Find all connection paths from Purecycle Technologies employees to JobsOhio",
      },
      {
        prompt:
          "Summarize recent federal environmental regulation changes impacting shale energy and petrochemicals, including key updates on drilling rules, EPA chemical reporting, and NEPA regulation revisions.",
      },
      {
        prompt:
          "Analyze recent news and partnerships of top aerospace companies in Ohio and their impact on job creation",
      },
    ],
  },
];

const countPrompts = (sections) =>
  sections.reduce((n, s) => n + s.items.length, 0);

const filterPromptSections = (sections, q) => {
  if (!q.trim()) return sections;
  const lower = q.toLowerCase();
  return sections
    .map((s) => ({
      ...s,
      items: s.items.filter(
        (i) =>
          i.prompt.toLowerCase().includes(lower) ||
          (i.sector && i.sector.toLowerCase().includes(lower))
      ),
    }))
    .filter((s) => s.items.length > 0);
};

const AtlasPromptLibrary = () => {
  const [query, setQuery] = useState("");
  const [activeSectionId, setActiveSectionId] = useState("lead-gen");
  const [copiedKey, setCopiedKey] = useState(null);

  const sections = useMemo(() => promptSections, []);
  const filteredSections = useMemo(
    () => filterPromptSections(sections, query),
    [sections, query]
  );
  const totalPrompts = useMemo(() => countPrompts(sections), [sections]);

  useEffect(() => {
    const first = filteredSections[0]?.id || "lead-gen";
    setActiveSectionId(first);
  }, [filteredSections]);

  const scrollToSection = (id) => {
    setActiveSectionId(id);
    const el = document.getElementById(`pd-sec-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 1500);
  };

  return (
    <Layout>
      <div className="hc-shell">
        <div className="hc-layout" role="tabpanel" aria-label="Atlas Prompt Library">
          {/* Left navigation */}
          <aside className="hc-nav" aria-label="Prompt Library navigation">
            <div className="hc-nav-card">
              <div className="hc-search">
                <i className="fas fa-magnifying-glass" aria-hidden="true" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search prompts…"
                  aria-label="Search prompts"
                />
                {query.trim() && (
                  <button
                    type="button"
                    className="hc-clear"
                    onClick={() => setQuery("")}
                    aria-label="Clear search"
                    title="Clear"
                  >
                    <i className="fas fa-times" />
                  </button>
                )}
              </div>

              <div className="hc-nav-title">Categories</div>
              <div className="hc-nav-list">
                {filteredSections.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    className={`hc-nav-item ${activeSectionId === s.id ? "active" : ""}`}
                    onClick={() => scrollToSection(s.id)}
                  >
                    <span className="hc-nav-item-text">{s.title}</span>
                    <span className="hc-nav-item-count">{s.items.length}</span>
                  </button>
                ))}
              </div>

              <div className="hc-nav-tip">
                <i className="fas fa-shield-halved pd-tip-icon" aria-hidden="true" />{" "}
                Atlas Chat follows strict access controls and will generate responses only from data sources and content that the user is authorized to access.
              </div>
            </div>
          </aside>

          {/* Main content */}
          <main className="hc-main">
            <div className="pd-intro-card">
              <div className="pd-intro-title">JobsOhio Sector Prompt Library</div>
              <div className="pd-intro-tagline">AI-Assisted Research Prompts by Category &amp; Sector</div>
              <div className="pd-intro-divider" />
              <div className="pd-intro-how-label">How to Use This Document</div>
              <div className="pd-intro-sub">
                Sample prompts to help you explore and effectively utilize Atlas Chat. Modify as needed for your specific target company, geography, or sector focus. Prompts are organized into categories, each mapped to core JobsOhio sectors.
              </div>
              <div className="pd-intro-stat">
                <span className="hc-pill">{totalPrompts}</span> prompts across{" "}
                <span className="hc-pill">{sections.length}</span> categories
              </div>
            </div>

            {filteredSections.length === 0 ? (
              <div className="hc-empty">
                <div className="hc-empty-title">No prompts found</div>
                <div className="hc-empty-sub">Try a different keyword.</div>
              </div>
            ) : (
              filteredSections.map((section) => (
                <section
                  key={section.id}
                  id={`pd-sec-${section.id}`}
                  className="hc-section"
                  aria-label={section.title}
                >
                  <div className="hc-section-head">
                    <div className="hc-section-title">{section.title}</div>
                    <div className="hc-section-meta">
                      {section.items.length}{" "}
                      {section.items.length === 1 ? "prompt" : "prompts"}
                    </div>
                  </div>

                  <div className="pd-section-desc">{section.description}</div>

                  <div className="pd-prompt-list">
                    {section.items.map((item, idx) => {
                      const key = `${section.id}:${idx}`;
                      const isCopied = copiedKey === key;

                      return (
                        <div key={key} className="pd-prompt-card">
                          <div className="pd-prompt-body">
                            {item.sector && (
                              <div className="pd-prompt-sector">{item.sector}</div>
                            )}
                            <div className="pd-prompt-text">{item.prompt}</div>
                          </div>
                          <div className="pd-prompt-actions">
                            <button
                              type="button"
                              className="pd-copy-btn"
                              onClick={() => handleCopy(item.prompt, key)}
                            >
                              <i
                                className={`fas ${isCopied ? "fa-check" : "fa-copy"}`}
                                aria-hidden="true"
                              />
                              <span>{isCopied ? "Copied!" : "Copy Prompt"}</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))
            )}
          </main>
        </div>
      </div>
    </Layout>
  );
};

export default AtlasPromptLibrary;
